import type { ComponentChildren } from 'preact'
import { useId } from 'preact/hooks'
import { SQUIRCLE_PATH } from './squircle.ts'

export interface AppIconProps {
  readonly class?: string | undefined
}

interface TileProps extends AppIconProps {
  /** Gradient stops, as OS tokens. */
  readonly top: string
  readonly bottom: string
  readonly children: ComponentChildren
}

const GLYPH = { fill: 'var(--os-icon-glyph)', stroke: 'var(--os-icon-glyph)' }

/** A squircle tile with a vertical gradient and a white mark: every app icon's shape. */
function Tile({ class: className, top, bottom, children }: TileProps) {
  // One gradient per instance: the same icon shows in the grid and the dock at once.
  const id = useId()
  return (
    <svg class={className} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: top }} />
          <stop offset="1" style={{ stopColor: bottom }} />
        </linearGradient>
      </defs>
      <path d={SQUIRCLE_PATH} fill={`url(#${id})`} />
      <g style={GLYPH}>{children}</g>
    </svg>
  )
}

/** A speech-bubble outline, its tail bottom left, with a rounded "w" where the handset would be. */
export function WassupIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-wassup-top)" bottom="var(--app-wassup-bottom)">
      <path
        d="M29.25 57.1A24 24 0 1 1 40.86 68.75L27 73Z"
        fill="none"
        stroke-width="5.5"
        stroke-linejoin="round"
      />
      <path
        d="M38 41l5 15 7-11 7 11 5-15"
        fill="none"
        stroke-width="5.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </Tile>
  )
}

/** A bold "in" whose i-dot is a small padlock. */
export function LockedInIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-lockedin-top)" bottom="var(--app-lockedin-bottom)">
      <path
        d="M29.5 47V76M49.5 76V47M49.5 58c0-7 5-11 11-11s10.5 4 10.5 11V76"
        fill="none"
        stroke-width="11"
      />
      <rect x="22.5" y="29.5" width="14" height="11" rx="2.5" stroke="none" />
      <path d="M25.5 30v-3.5a4 4 0 0 1 8 0V30" fill="none" stroke-width="2.6" />
    </Tile>
  )
}

/** A heavy rounded "grub" wordmark over a fork. */
export function GrubFoodIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-grubfood-top)" bottom="var(--app-grubfood-bottom)">
      <text
        x="50"
        y="53"
        text-anchor="middle"
        stroke="none"
        style={{ font: '800 31px var(--font-sans)', letterSpacing: '-1.5px' }}
      >
        grub
      </text>
      <path
        d="M27 68H59M59 62.5V73.5M59 62.5H73M59 68H73M59 73.5H73"
        fill="none"
        stroke-width="3.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </Tile>
  )
}

/** A heavy green "24" over three stripes, orange, green and red, on a white tile. */
export function TwentyFourSevenIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-24seven-top)" bottom="var(--app-24seven-bottom)">
      <text
        x="50"
        y="57"
        text-anchor="middle"
        stroke="none"
        style={{
          font: '900 44px var(--font-sans)',
          letterSpacing: '-3px',
          fill: 'var(--app-24seven-mark)',
        }}
      >
        24
      </text>
      <g stroke="none">
        <rect
          x="16"
          y="64"
          width="68"
          height="6"
          rx="3"
          style={{ fill: 'var(--app-24seven-orange)' }}
        />
        <rect
          x="16"
          y="71"
          width="68"
          height="6"
          rx="3"
          style={{ fill: 'var(--app-24seven-green)' }}
        />
        <rect
          x="16"
          y="78"
          width="68"
          height="6"
          rx="3"
          style={{ fill: 'var(--app-24seven-red)' }}
        />
      </g>
    </Tile>
  )
}

/** A form with a stamp: three lines of small print and a round "approved" mark. */
export function WellfareIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-wellfare-top)" bottom="var(--app-wellfare-bottom)">
      <path d="M30 20h30l12 12v48H30Z" stroke="none" />
      <path
        d="M60 20v12h12"
        fill="none"
        stroke-width="3"
        style={{ stroke: 'var(--app-wellfare-fold)' }}
      />
      <path
        d="M37 39h22M37 47h28M37 55h16"
        fill="none"
        stroke-width="3.5"
        stroke-linecap="round"
        style={{ stroke: 'var(--app-wellfare-print)' }}
      />
      <g style={{ stroke: 'var(--app-wellfare-stamp)' }} fill="none">
        <circle cx="63" cy="68" r="11" stroke-width="3.5" />
        <path
          d="M57.5 68l4 4 7-8"
          stroke-width="3.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </Tile>
  )
}

/** A white folder. */
export function FilesIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-files-top)" bottom="var(--app-files-bottom)">
      <path
        d="M21 37a5 5 0 0 1 5-5h15l6 6h27a5 5 0 0 1 5 5v25a5 5 0 0 1-5 5H26a5 5 0 0 1-5-5Z"
        stroke="none"
      />
      <path
        d="M21 46H79"
        fill="none"
        stroke-width="2"
        style={{ stroke: 'var(--app-files-fold)' }}
      />
    </Tile>
  )
}

/** A white handset. */
export function PhoneIcon({ class: className }: AppIconProps) {
  return (
    <Tile class={className} top="var(--app-phone-top)" bottom="var(--app-phone-bottom)">
      <path d="M33 31Q27 72 69 67" fill="none" stroke-width="9" stroke-linecap="round" />
      <rect
        x="24"
        y="22"
        width="19"
        height="13"
        rx="6.5"
        stroke="none"
        transform="rotate(-8 33.5 28.5)"
      />
      <rect
        x="63"
        y="58"
        width="13"
        height="19"
        rx="6.5"
        stroke="none"
        transform="rotate(-8 69.5 67.5)"
      />
    </Tile>
  )
}
